'use client';

import Image from 'next/image';
import {
  AnimatePresence,
  motion,
  useScroll,
  type MotionValue,
} from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect, useId, useRef, useState, type Ref } from 'react';
import { CapsuleButton } from '@/components/button/capsule-button';
import { TrackedButton } from '@/components/button/tracked-button';
import { TrackedAnchor } from '@/components/ancher/tracked-anchor';
import { useInquiryChat } from '@/components/channel-talk';
import { TRACKED_EVENT_NAME, TRACKED_EVENT_POSITION } from '@/constants/gtag';
import { useLiteMode } from '@/hooks/useLiteMode.hook';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { MAIN_XL_MAX_QUERY, MAIN_XL_MIN_QUERY } from '@/constants/main';
import { alwaysBreakRichTextTags, customRichTextTags } from './main-responsive';
import ScrollStage from './_components/scroll-stage';
import MobileSectionEclipse from './_components/mobile-section-eclipse';
import DesktopSectionEclipse from './_components/desktop-section-eclipse';

const ITEMS = [
  { key: 'kit', image: 'kit.webp', chip: 'order-now' },
  { key: 'boosted-exome', image: 'wes.webp', chip: 'view-details' },
  {
    key: 'report',
    image: 'report-folder.webp',
    chip: 'sample-report',
  },
  { key: 'consult', image: 'clinician.webp', chip: 'inquiry' },
] as const;
const CONSULT_LABEL = 'main-order_clinical-genetics-consult';
const chipClass =
  'h-9 gap-0 border-0 px-3 py-2 text-[12px] leading-3 tracking-[-0.04px] xl:h-10 xl:gap-1 xl:px-5 xl:py-2.5 xl:text-[14px] xl:leading-4 xl:tracking-[-0.128px] hover:bg-[#e60020] focus-visible:bg-[#e60020]';

function OrderAction({ index }: { index: number }) {
  const t = useTranslations('Main.order');
  const inquiryChatProps = useInquiryChat();
  const item = ITEMS[index];
  const contents = (
    <>
      <span className="px-1">{t(`${item.key}.cta`)}</span>
      <Image
        src="/images/main/icons/chip-tail.svg"
        alt=""
        width={20}
        height={20}
      />
    </>
  );
  if (item.key === 'consult') {
    return (
      <CapsuleButton asChild className={chipClass}>
        <TrackedButton
          {...inquiryChatProps}
          label={CONSULT_LABEL}
          position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
          eventName={TRACKED_EVENT_NAME.CTA_CLICK}
        >
          {contents}
        </TrackedButton>
      </CapsuleButton>
    );
  }
  return (
    <CapsuleButton asChild className={chipClass}>
      <TrackedAnchor
        href={t(`${item.key}.href`)}
        target="_blank"
        label={`main-order_chip-${item.chip}`}
        position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
        eventName={
          item.key === 'kit'
            ? TRACKED_EVENT_NAME.CTA_CLICK
            : TRACKED_EVENT_NAME.CLICK_LINK
        }
      >
        {contents}
      </TrackedAnchor>
    </CapsuleButton>
  );
}

// 스크롤 진행률을 항목 수만큼 균등 분할해 활성 항목을 정한다.
const STEP = 1 / ITEMS.length;
const toStep = (value: number) =>
  Math.min(ITEMS.length - 1, Math.max(0, Math.floor(value / STEP)));

function OrderContent({
  progress,
  instant = false,
  scrollToProgress,
  listRef,
}: {
  // 넘어오면 스크롤 진행률이 활성 항목을 구동한다. 없으면 클릭으로만 바뀐다.
  progress?: MotionValue<number>;
  instant?: boolean;
  // 고정(sticky) 구간에서만 쓴다 — 선택한 항목에 맞춰 스크롤 위치를 함께 옮긴다.
  scrollToProgress?: (value: number) => void;
  listRef?: Ref<HTMLDivElement>;
}) {
  const t = useTranslations('Main.order');
  const id = useId();
  const [active, setActive] = useState(0);
  // 스크롤 위치를 옮길 수 없는 구간에서 수동 선택이 곧바로 되돌려지지 않도록,
  // 선택 시점의 스크롤 구간을 기억해 두고 그 구간을 벗어날 때 동기화를 재개한다.
  const heldStepRef = useRef<number | null>(null);
  // progress 는 부모(ScrollStage)의 렌더 도중에도 갱신되므로, 곧바로 상태를 바꾸면
  // 다른 컴포넌트를 렌더하는 중에 setState 하게 된다. 프레임당 한 번만 맞춘다.
  useEffect(() => {
    if (!progress) return;
    let frame: number | undefined;
    const apply = () => {
      frame = undefined;
      const step = toStep(progress.get());
      if (heldStepRef.current === step) return;
      heldStepRef.current = null;
      setActive(step);
    };
    const schedule = () => {
      if (frame === undefined) frame = requestAnimationFrame(apply);
    };
    apply();
    const unsubscribe = progress.on('change', schedule);
    return () => {
      unsubscribe();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [progress]);

  // 제목 클릭 시 해당 항목 구간의 중앙으로 즉시 이동해 이후 스크롤이 자연스럽게 이어지게 한다.
  // 고정 구간이 아니면(모바일) 화면이 튀므로 옮기지 않고, 대신 선택을 유지한다.
  const select = (index: number) => {
    setActive(index);
    if (!progress) return;
    if (scrollToProgress) {
      heldStepRef.current = null;
      scrollToProgress((index + 0.5) * STEP);
      return;
    }
    heldStepRef.current = toStep(progress.get());
  };

  return (
    <div className="relative flex min-h-[inherit] flex-col items-center justify-start gap-10 bg-white px-5 pb-20 pt-20 xl:justify-center xl:gap-20 xl:px-6 xl:py-[clamp(24px,calc((var(--scroll-stage-height,100svh)-550px)/2),180px)] [@media(max-height:900px)]:gap-10">
      <DesktopSectionEclipse half="lower" />
      {/* 고정된 동안 헤더(반투명) 뒤가 비지 않게, 위 섹션의 타원 윗절반을 헤더 높이만큼 이어 붙인다.
          ScrollStage 의 clip-path 가 고정 전엔 잘라내 위 섹션의 타원과 겹치지 않는다 */}
      {progress ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-full hidden h-[var(--scroll-stage-top)] overflow-hidden xl:block"
        >
          <DesktopSectionEclipse half="upper" />
        </div>
      ) : null}
      <MobileSectionEclipse half="lower" />
      <h2 className="relative text-center text-[24px] font-semibold leading-8 tracking-[-0.464px] text-[#040435] xl:text-[48px] xl:leading-[56px] xl:tracking-[-1.072px]">
        {t.rich('title', alwaysBreakRichTextTags)}
      </h2>
      <div className="grid w-full max-w-[828px] items-center gap-10 xl:min-h-[358px] xl:grid-cols-[398px_340px] xl:gap-[90px]">
        <div
          ref={listRef}
          className="order-2 mx-auto flex w-full max-w-[310px] flex-col gap-6 xl:order-none xl:max-w-none"
        >
          {ITEMS.map((item, index) => (
            <div key={item.key}>
              <h3>
                <TrackedButton
                  type="button"
                  id={`${id}-heading-${index}`}
                  aria-expanded={active === index}
                  aria-controls={`${id}-panel-${index}`}
                  label={`main-order_tab-${item.key}`}
                  position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
                  onClick={() => select(index)}
                  className="group flex w-full items-center justify-center gap-3 rounded text-center text-[16px] font-semibold leading-6 tracking-[-0.18px] text-3b-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#040435] xl:justify-start xl:whitespace-nowrap xl:text-left xl:text-[24px] xl:leading-[22px] xl:tracking-[-0.464px]"
                >
                  <span
                    aria-hidden="true"
                    className="hidden h-5 w-1.5 shrink-0 rounded-[20px] bg-[#ff142d] opacity-0 transition-opacity duration-300 group-aria-expanded:block group-aria-expanded:opacity-100 motion-reduce:transition-none xl:block xl:h-6 xl:w-2"
                  />
                  <span className="opacity-20 transition-opacity duration-300 group-hover:opacity-80 group-aria-expanded:opacity-100 motion-reduce:transition-none xl:opacity-70">
                    {t(`${item.key}.title`)}
                  </span>
                </TrackedButton>
              </h3>
              <div
                id={`${id}-panel-${index}`}
                aria-labelledby={`${id}-heading-${index}`}
                aria-hidden={active !== index}
                inert={active !== index}
                role="region"
                className="xl:pl-5"
              >
                <AnimatePresence initial={false}>
                  {active === index && (
                    <motion.div
                      key={item.key}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        duration: instant ? 0 : 0.35,
                        ease: 'easeInOut',
                      }}
                      className="overflow-hidden"
                    >
                      <div className="px-[18px] pb-1 pt-2 xl:px-0 xl:pt-0.5">
                        <p className="break-keep text-center text-[14px] font-normal leading-[18px] tracking-[-0.128px] text-3b-text-secondary xl:whitespace-nowrap xl:text-left xl:text-[16px] xl:font-medium xl:leading-[26px] xl:tracking-[-0.176px]">
                          {t.rich(
                            `${item.key}.description`,
                            customRichTextTags,
                          )}
                        </p>
                        <div className="mt-2.5 flex min-h-9 justify-center xl:mt-4 xl:min-h-10 xl:justify-start">
                          <OrderAction index={index} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
        <div
          aria-hidden="true"
          className="relative mx-auto aspect-[200/204.286] w-full max-w-[200px] xl:aspect-square xl:max-w-[340px]"
        >
          {ITEMS.map((item, index) => (
            <motion.div
              key={item.key}
              className="absolute inset-0"
              initial={false}
              animate={{ opacity: active === index ? 1 : 0 }}
              transition={{ duration: instant ? 0 : 0.35, ease: 'easeInOut' }}
            >
              <Image
                src={`/images/main/order/${item.image}`}
                alt=""
                fill
                sizes={`${MAIN_XL_MIN_QUERY} 340px, 200px`}
                className="object-contain"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileOrder({ instant }: { instant: boolean }) {
  const listRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ['start 85%', 'end 70%'],
  });

  return (
    <OrderContent
      progress={scrollYProgress}
      instant={instant}
      listRef={listRef}
    />
  );
}

// 모바일 고정 구간 길이(화면 높이 대비 %). 항목 넘김 속도를 여기서 조절한다
const MOBILE_STAGE_HEIGHT_VH = 250;
// 고정 시 화면 위로 넘겨도 되는 높이 — 위 여백(80)+제목(64)+제목 아래 간격(40)+여유
const MOBILE_MAX_OVERFLOW_PX = 200;
// 데스크톱은 낮은 노트북 화면(배너 포함)에서 제목 위 여백만큼 넘치는 경우까지만 고정한다
const DESKTOP_MAX_OVERFLOW_PX = 40;

export default function OrderSection() {
  const isLiteMode = useLiteMode();
  const isMobile = useMediaQuery(MAIN_XL_MAX_QUERY);
  return (
    <section data-main-order className="relative bg-white">
      {isLiteMode ? (
        isMobile ? (
          <MobileOrder instant />
        ) : (
          <OrderContent instant />
        )
      ) : (
        // 모바일도 화면에 고정해 두고 스크롤 거리로 항목을 넘긴다. 고정 없이 흘리면 목록 높이만큼의
        // 짧은 구간(약 380px)에 네 항목이 다 지나간다. 모바일 콘텐츠(약 770px)는 대부분의 폰 화면보다 커서
        // 제목 영역만큼은 화면 위로 넘긴 채 이미지·목록을 고정한다. 그보다 작은 화면이면 기존 방식으로 돌린다
        <ScrollStage
          heightVh={isMobile ? MOBILE_STAGE_HEIGHT_VH : 300}
          mobileUnderHeader
          maxOverflowPx={
            isMobile ? MOBILE_MAX_OVERFLOW_PX : DESKTOP_MAX_OVERFLOW_PX
          }
          className="[clip-path:inset(0)]"
        >
          {(progress, sticky, scrollToProgress) =>
            sticky ? (
              <OrderContent
                progress={progress}
                scrollToProgress={scrollToProgress}
              />
            ) : isMobile ? (
              <MobileOrder instant={false} />
            ) : (
              <OrderContent instant />
            )
          }
        </ScrollStage>
      )}
    </section>
  );
}
